import React, { useState } from 'react';
import { useDispatch } from 'react-redux';
import { useNavigate, useLocation } from 'react-router-dom';
import compose from 'lodash/fp/compose';
import styled from 'styled-components';
import Swal from 'sweetalert2';
import {
  Input,
  Select,
  InputDatePicker,
  FileInput,
  Form,
  ButtonLink,
} from 'components/common/FormItems';
import {
  withTranslation,
  withDocumentCategory,
  withLanguage,
  withOrganisation,
  withMsgInfo,
} from 'components/hoc';
import API from '../../../../actions/api';
import axios from '../../../../axios';
import { getFavoriteFormsThunk } from '../myFormsState/myFormsThunk';

const Wrapper = styled.div``;

const StyledButtonLink = styled(ButtonLink)`
  span {
    text-transform: uppercase;
  }
`;

const StyledInput = styled(Input)`
  padding-right: 0.5rem;
`;

const StyledSelect = styled(Select)`
  margin-right: 2rem;
`;

const StyledInputDatePicker = styled(InputDatePicker)`
  padding-right: 1rem;
`;

const StyledFileInput = styled(FileInput)`
  padding-right: 1rem;
`;

const extractErrorMessages = (data) => {
  if (!data) return null;
  const messages = [];
  const walk = (node) => {
    if (typeof node === 'string') messages.push(node);
    else if (Array.isArray(node)) node.forEach(walk);
    else if (typeof node === 'object') Object.values(node).forEach(walk);
  };
  walk(data);
  return messages
    .map((msg) =>
      msg.includes('Invalid pk') || msg.includes('object does not exist')
        ? 'One or more selected values are invalid. Please check your selections and try again.'
        : msg
    )
    .filter((msg, i, arr) => arr.indexOf(msg) === i) // deduplicate
    .join(' ');
};

const formSchema = {
  form_category: { validation: 'required' },
  language: { validation: 'required' },
};

const initialValues = {
  pdf_file: '',
  issuing_org: '',
  form_category: '',
  description: '',
  own_sign: '',
  language: '',
  valid_from: '',
  valid_to: '',
};

const ProposeFormPage = (props) => {
  const {
    tr,
    documentCategoryLocaleOptions,
    activeLanguageOptions,
    orgOptions,
    setMsgInfo,
  } = props;

  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();

  const [values, setValues] = useState({
    ...initialValues,
    ...(location.state?.prefill || {}),
  });
  const [formSubmitting, setFormSubmitting] = useState(false);

  const handleChange = (e) => {
    if (formSubmitting) return;
    const { name, value } = e.target;
    setValues((prev) => ({ ...prev, [name]: value }));
  };

  const onSubmit = async () => {
    if (formSubmitting) return;
    try {
      setFormSubmitting(true);

      const fData = new FormData();
      Object.entries(values).forEach(([key, value]) => {
        if (value !== '' && value !== null && value !== undefined) {
          fData.append(key, value);
        }
      });

      await axios.post(`${API}/dashboard/form`, fData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });

      dispatch(
        setMsgInfo({
          success: true,
          msg: [tr('Form proposal submitted successfully.')],
        })
      );
      dispatch(getFavoriteFormsThunk());
      navigate('/dashboard/my-forms/list');
      window.scrollTo(0, 0);
    } catch (error) {
      const msg = extractErrorMessages(error?.response?.data)
        || tr('Failed to submit form proposal. Please try again.');
      Swal.fire(tr('Error'), msg, 'error');
    } finally {
      setFormSubmitting(false);
    }
  };

  React.useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <Wrapper className="form-wrapper">
      <div className="flex">
        <StyledButtonLink
          to="/dashboard/my-forms/search"
          className="option-btn"
          icon="angle double left"
          name="Back to Search"
        />
        <StyledButtonLink
          to="/dashboard/my-forms/list"
          className="option-btn"
          icon="angle double left"
          name="All Entries"
        />
      </div>

      <div className="row">
        <div
          className="col-7 ml-10 mt-30 container"
          style={{
            position: 'relative',
            background: 'var(--admincat-color-grey-1)',
          }}
        >
          <br />

          <Form
            onSubmit={onSubmit}
            formData={values}
            validationSchema={formSchema}
            isLoading={formSubmitting}
            title={tr('Propose Form')}
          >
            <StyledFileInput
              onChange={handleChange}
              value={values.pdf_file}
              name="pdf_file"
              label={tr('PDF File')}
              accept=".pdf"
            />

            <StyledSelect
              onChange={handleChange}
              name="issuing_org"
              label={tr('Issuing Organization')}
              optionDefaultValue={tr('Choose Organization')}
              options={orgOptions}
              value={values.issuing_org}
              deselectable
              sort={false}
            />

            <StyledSelect
              onChange={handleChange}
              name="form_category"
              label={tr('Form Category')}
              optionDefaultValue={tr('Choose Form Category')}
              localeOptions={documentCategoryLocaleOptions}
              value={values.form_category}
              showRequiredAsterisk
            />

            <StyledInput
              onChange={handleChange}
              name="description"
              label={tr('Description')}
              placeholder={tr('Description')}
              value={values.description}
            />

            <StyledInput
              onChange={handleChange}
              name="own_sign"
              label={tr('Own Sign')}
              placeholder={tr('Own Sign')}
              value={values.own_sign}
            />

            <StyledSelect
              onChange={handleChange}
              name="language"
              label={tr('Language')}
              optionDefaultValue={tr('Choose Language')}
              options={activeLanguageOptions}
              value={values.language}
              showRequiredAsterisk
            />

            <StyledInputDatePicker
              label={tr('Valid From')}
              value={values.valid_from}
              name="valid_from"
              onChange={handleChange}
            />

            <StyledInputDatePicker
              label={tr('Valid To')}
              value={values.valid_to}
              name="valid_to"
              onChange={handleChange}
            />
          </Form>
        </div>
      </div>
    </Wrapper>
  );
};

export default compose(
  withTranslation,
  withDocumentCategory,
  withLanguage,
  withOrganisation,
  withMsgInfo,
)(ProposeFormPage);
